import BuildConfig from '@/environment';
import i18n from '@/plugins/i18n';
import { getAlertsV2NavTabs } from '@/utils/templates/alertsV2.template';
import { getApmNavTabs } from '@/utils/templates/apm.template';
import { getDataUsageNavTabs } from '@/utils/templates/billing.usage.template';
import { getBMNavTabs } from '@/utils/templates/browser.template';
import { getDBMNavTabs } from '@/utils/templates/database.template';
import { getMMNavTabs } from '@/utils/templates/mobile.template';
import { getMQNavTabs } from '@/utils/templates/mq.template';
import { getSettingsNavTabs } from '@/utils/templates/settings.template';
import { getSMNavTabs } from '@/utils/templates/sm.template';

const timeLabels = {
  fiveMins: '5 mins',
  oneHour: '60 mins',
  oneMonth: '1 month',
  oneWeek: '1 week',
  custom: 'Custom',
};
const docsBaseUrl = BuildConfig.VUE_APP_DOCS_BASE_URL;
const docs = {
  home: `${docsBaseUrl}/observability/docs/overview`,
  apm: `${docsBaseUrl}/observability/docs/apm/overview-apm`,
  browser: `${docsBaseUrl}/observability/docs/browser/overview-browser`,
  sm: `${docsBaseUrl}/observability/docs/synthetic/overview-synthetic`,
  database: `${docsBaseUrl}/observability/docs/database/overview-database`,
  vm: `${docsBaseUrl}/observability/docs/vm/overview-vm`,
  mobile: `${docsBaseUrl}/observability/docs/mobile/overview-mobile`,
  wsm: `${docsBaseUrl}/docs/webserver/web-server-dashboard`,
  k8m: `${docsBaseUrl}/observability/docs/kubernetes/overview-kubernetes`,
  cloud: `${docsBaseUrl}/observability/docs/integrations/install-gke`,
  log: `${docsBaseUrl}/observability/docs/log-monitoring/overview-log`,
  integrations: `${docsBaseUrl}/observability/docs/integrations/install-gke`,
  settings: `${docsBaseUrl}/observability/docs/overview/`,
  notification: `${docsBaseUrl}/observability/docs/alerts-notifications/alerts-overview`,
  alert: `${docsBaseUrl}/observability/docs/alerts-notifications/alerts-overview`,
  billingUsage: `${docsBaseUrl}/observability/docs/usage/usage-overview`,
  digitalExperience: `${docsBaseUrl}/observability/docs/digital-experience/dig-exp-overview`,
  mq: `${docsBaseUrl}/observability/docs/mq-monitoring/overview`,
  analytics: `${docsBaseUrl}/observability/docs/analytics/overview-analytics`,
};

const toolbarTimeOptions = [
  { value: 60 * 5 * 1000, name: timeLabels.fiveMins },
  { value: 60 * 15 * 1000, name: '15 mins' },
  { value: 60 * 30 * 1000, name: '30 mins' },
  { value: 3600 * 1000, name: '60 mins' },
  { value: 3600 * 1000 * 3, name: '3 hours', addDivider: true },
  { value: 3600 * 1000 * 6, name: '6 hours' },
  { value: 3600 * 1000 * 12, name: '12 hours' },
  { value: 3600 * 1000 * 24, name: '1 day', addDivider: true },
  { value: 3600 * 1000 * 24 * 7, name: '1 week' },
  { value: 3600 * 1000 * 24 * 30, name: timeLabels.oneMonth },
  {
    value: 'custom',
    name: timeLabels.custom,
    addDivider: true,
    iconReq: true,
    iconName: 'calendarSetting',
  },
];

const autoRefTimers = [
  { label: 'Off', id: 'Off' },
  { label: '15s', id: '15' },
  { label: '30s', id: '30' },
  { label: '1m', id: '60' },
  { label: '5m', id: '300' },
  { label: '15m', id: '900' },
];

export const codeProfileRouteBasedCalConfig = {
  routes: [
    'instanceThreads',
    'instanceMemory',
    'instanceSocketIO',
    'instanceMethodProfile',
    'garbageCollection',
    'gcConfiguration',
    'threads',
  ],
  value: 3600 * 1000 * 3,
};

const langSelectFieldOptions = [
  { label: 'English', value: 'en' },
  { label: '日本語', value: 'ja' },
];

const currSidenavMapper = (name) => {
  let nameLangMapper = {
    Home: i18n.global.t('home'),
    Alerts: i18n.global.t('alerts'),
    'Notification channels': i18n.global.t('notificationChannels'),
    APM: i18n.global.t('apmLabel'),
    'Database monitoring': i18n.global.t('databaseMonitoring'),
    'Synthetic endpoint monitoring': i18n.global.t('syntheticMonitoring'),
    'Browser monitoring': i18n.global.t('browserMonitoring'),
    'VM monitoring': i18n.global.t('vmMonitoring'),
    'Mobile monitoring': i18n.global.t('mobileMonitoring'),
    'Kubernetes monitoring': i18n.global.t('k8m'),
    Settings: i18n.global.t('settings'),
    Integrations: i18n.global.t('integrations'),
    'Cloud monitoring': i18n.global.t('cloudSvc'),
    'Log monitoring': i18n.global.t('logMonitoring'),
    Analytics: i18n.global.t('analytics.analytics'),
    'Digital experience': i18n.global.t('digitalExperience'),
    'Usage dashboard': i18n.global.t('usage-dashboard'),
    'MQ monitoring': i18n.global.t('mq.mqMonitoring'),
  };
  return nameLangMapper[name] || name;
};

const navTabsMapper = {
  alertsV2: getAlertsV2NavTabs,
  apm: getApmNavTabs,
  usages: getDataUsageNavTabs,
  browser: getBMNavTabs,
  database: getDBMNavTabs,
  mobile: getMMNavTabs,
  mq: getMQNavTabs,
  settings: getSettingsNavTabs,
  sm: getSMNavTabs,
};

export {
  toolbarTimeOptions,
  autoRefTimers,
  timeLabels,
  docs,
  langSelectFieldOptions,
  currSidenavMapper,
  navTabsMapper,
};
